import { isPrivateProductMediaKey } from "./media";
import { uniqueMediaAssetsForPublication } from "./providers/provider-publication-media";
import { sanitizeDurableMarketplaceImageUrl, sanitizeMarketplaceImageUrl } from "./template-url";

/**
 * Preview vs publication media contract:
 * - Preview may show temporary signed HTTPS URLs for private product-media so
 *   the merchant sees the same assets (count/order) they expect to publish.
 *   Those signed URLs are display-only and never treated as publication media.
 * - Publication must use durable provider-public HTTPS URLs produced by
 *   materializePublicationMedia. If those cannot be produced, publishers fall
 *   back to plain-description HTML instead of an image-less template.
 * - URLs are never fabricated.
 */
export interface ListingTemplateMediaSource {
  media_asset_id?: string | null;
  storage_key?: string | null;
  alt_text?: string | null;
  public_url?: string | null;
  url?: string | null;
}

export interface ListingTemplatePreviewImage {
  url: string;
  alt: string | null;
  /** True when the src is a temporary signed URL for operator preview only. */
  displayOnlySigned: boolean;
}

export function listingTemplatePreviewDisplayImages(
  media: ListingTemplateMediaSource[],
  signedUrlsByStorageKey: Record<string, string>,
): ListingTemplatePreviewImage[] {
  const out: ListingTemplatePreviewImage[] = [];
  for (const row of media ?? []) {
    const durable = sanitizeDurableMarketplaceImageUrl(String(row.public_url || row.url || ""));
    if (durable) {
      out.push({ url: durable, alt: row.alt_text ?? null, displayOnlySigned: false });
      continue;
    }
    const key = typeof row.storage_key === "string" ? row.storage_key : "";
    if (key && isPrivateProductMediaKey(key)) {
      const signed = sanitizeMarketplaceImageUrl(signedUrlsByStorageKey[key] || "");
      if (signed) {
        out.push({ url: signed, alt: row.alt_text ?? null, displayOnlySigned: true });
      }
    }
  }
  return out;
}

export function listingTemplatePublicationMediaIds(media: unknown): string[] {
  return uniqueMediaAssetsForPublication(media).map((row) => row.media_asset_id);
}
